import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BRAND_KIT } from "@/constants/brandKitTestIds";
import {
  Field,
  TextInput,
  TextArea,
  NumberInput,
  BindingPillGroup,
  MultiSelectChips,
  FormActions,
} from "./Primitives";
import { _Header as Header, _Empty as Empty, _RowActions as RowActions } from "./LogosTab";
import ContrastChecker from "./ContrastChecker";
import useBindings from "@/lib/useBindings";
import useContentCategories from "@/lib/useContentCategories";

const PATH = "colors";

const EMPTY = {
  name: "",
  hex: "#1A1F2C",
  cmyk: "",
  pantone: "",
  role_tags: [],
  description: "",
  priority: 100,
  allowed_background_color_ids: [],
  min_contrast_against_color_ids: [],
  usable_in_category_slugs: [],
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function ColorsTab({ brandId, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { bindings, loading: bindingsLoading } = useBindings("color", brandId);
  const { categories } = useContentCategories(brandId);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/brands/${brandId}/brand-kit/${PATH}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [brandId]);

  // Options for the disambiguation pickers — derived from current palette.
  const colorOptions = useMemo(
    () =>
      items
        .filter((c) => c.color_id !== editing)
        .map((c) => ({ value: c.color_id, label: c.name, swatch: c.hex })),
    [items, editing],
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const openNew = () => {
    setForm({ ...EMPTY, priority: (items.length + 1) * 10 });
    setEditing("new"); setError("");
  };
  const openEdit = (it) => {
    setForm({
      name: it.name, hex: it.hex, cmyk: it.cmyk || "", pantone: it.pantone || "",
      role_tags: it.role_tags || [], description: it.description || "",
      priority: it.priority ?? 100,
      allowed_background_color_ids: it.allowed_background_color_ids || [],
      min_contrast_against_color_ids: it.min_contrast_against_color_ids || [],
      usable_in_category_slugs: it.usable_in_category_slugs || [],
    });
    setEditing(it.color_id); setError("");
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!HEX_RE.test(form.hex)) {
      setError("HEX must be in the form #RRGGBB"); return;
    }
    setSubmitting(true);
    try {
      if (editing === "new") await api.post(`/brands/${brandId}/brand-kit/${PATH}`, form);
      else await api.put(`/brands/${brandId}/brand-kit/${PATH}/${editing}`, form);
      close(); await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save color");
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/brands/${brandId}/brand-kit/${PATH}/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete color");
    }
  };

  if (loading) return <p className="text-sm text-[#6B6358]">Loading colors…</p>;

  return (
    <div>
      <Header
        title="Colors"
        subtitle="The approved palette. Bindings come from the registry and grow as new layouts are registered. Lower priority wins when multiple colors share a binding; constraint fields let the AI Assembly Agent disambiguate by context."
        canManage={canManage}
        actionTestId={BRAND_KIT.colorAddButton}
        onAdd={openNew}
        active={editing !== null}
      />

      {editing !== null && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">
            {editing === "new" ? "New color" : "Edit color"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                testid={BRAND_KIT.colorNameInput} required placeholder="e.g. Primary Brand Teal" />
            </Field>
            <Field label="HEX">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={HEX_RE.test(form.hex) ? form.hex : "#000000"}
                  onChange={(e) => setForm({ ...form, hex: e.target.value.toUpperCase() })}
                  className="w-10 h-10 border border-[#1A1F2C]/20 rounded-md cursor-pointer bg-transparent"
                  aria-label="Pick color"
                />
                <input
                  data-testid={BRAND_KIT.colorHexInput}
                  type="text"
                  required
                  value={form.hex}
                  onChange={(e) => setForm({ ...form, hex: e.target.value.toUpperCase() })}
                  className="flex-1 px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] font-mono uppercase"
                  placeholder="#1A1F2C"
                />
              </div>
            </Field>
            <Field label="CMYK (optional)">
              <TextInput value={form.cmyk} onChange={(v) => setForm({ ...form, cmyk: v })}
                testid={BRAND_KIT.colorCmykInput} placeholder="C:75 M:25 Y:30 K:50" />
            </Field>
            <Field label="Pantone (optional)">
              <TextInput value={form.pantone} onChange={(v) => setForm({ ...form, pantone: v })}
                testid={BRAND_KIT.colorPantoneInput} placeholder="PMS 7474 C" />
            </Field>
            <Field label="Binding tags (where this color may be used)" className="md:col-span-2">
              <BindingPillGroup
                bindings={bindings}
                loading={bindingsLoading}
                selected={form.role_tags}
                onChange={(v) => setForm({ ...form, role_tags: v })}
                testidPrefix={BRAND_KIT.colorRoleTogglePrefix}
              />
            </Field>
            <Field label="Usage notes (optional)" className="md:col-span-2">
              <TextArea value={form.description} onChange={(v) => setForm({ ...form, description: v })}
                testid={BRAND_KIT.colorDescInput} rows={2}
                placeholder="When to use this color and pairings to avoid…" />
            </Field>
          </div>

          <div className="border-t border-[#1A1F2C]/10 mt-6 pt-5">
            <div className="flex items-baseline justify-between mb-1">
              <h4 className="font-serif text-base">AI Assembly disambiguation</h4>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">advanced</span>
            </div>
            <p className="text-xs text-[#6B6358] mb-4 max-w-2xl">
              Used by the AI Assembly Agent when multiple colors share a binding. It first filters
              by these constraints, then picks the lowest <em>priority</em> integer among survivors.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Priority (lower = picked first)">
                <NumberInput
                  value={form.priority}
                  onChange={(v) => setForm({ ...form, priority: v ?? 100 })}
                  testid={BRAND_KIT.colorPriorityInput}
                  step="1"
                  placeholder="100"
                />
              </Field>
              <div />
              <Field label="Only usable on these background colors" className="md:col-span-2">
                <MultiSelectChips
                  options={colorOptions}
                  selected={form.allowed_background_color_ids}
                  onChange={(v) => setForm({ ...form, allowed_background_color_ids: v })}
                  testidPrefix={BRAND_KIT.colorAllowedBgPrefix}
                  emptyLabel="Add at least one other color to use as a background option."
                />
              </Field>
              <Field label="Must clear WCAG contrast against" className="md:col-span-2">
                <MultiSelectChips
                  options={colorOptions}
                  selected={form.min_contrast_against_color_ids}
                  onChange={(v) => setForm({ ...form, min_contrast_against_color_ids: v })}
                  testidPrefix={BRAND_KIT.colorContrastPrefix}
                  emptyLabel="Add at least one other color to require contrast against."
                />
              </Field>
              <Field label="Restrict to content categories (optional)" className="md:col-span-2">
                <MultiSelectChips
                  options={categoryOptions}
                  selected={form.usable_in_category_slugs}
                  onChange={(v) => setForm({ ...form, usable_in_category_slugs: v })}
                  testidPrefix={BRAND_KIT.colorCategoryPrefix}
                  emptyLabel="No content categories defined for this brand yet."
                />
              </Field>
            </div>
          </div>

          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <FormActions submitting={submitting} onCancel={close}
            submitTestId={BRAND_KIT.colorSubmit} cancelTestId={BRAND_KIT.colorCancel} />
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid={BRAND_KIT.colorsEmpty} label="No colors yet." />
      ) : (
        <>
          <ContrastChecker colors={items} />
          <div data-testid={BRAND_KIT.colorsList} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => (
              <div
                key={it.color_id}
                data-testid={`${BRAND_KIT.colorRowPrefix}${it.color_id}`}
                className="border border-[#1A1F2C]/10 bg-white rounded-lg overflow-hidden"
              >
                <div
                  className="aspect-[4/3] w-full"
                  style={{ backgroundColor: HEX_RE.test(it.hex) ? it.hex : "#ccc" }}
                  aria-label={`Swatch ${it.hex}`}
                />
                <div className="p-4">
                  <div className="flex items-baseline justify-between gap-1">
                    <div className="font-serif text-base leading-tight">{it.name}</div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] shrink-0">P {it.priority ?? 100}</span>
                  </div>
                  <div className="font-mono text-[11px] text-[#6B6358] mt-1 uppercase">{it.hex}</div>
                  {it.role_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {it.role_tags.map((r) => (
                        <span key={r} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#F8F5EE] text-[#6B6358]">
                          {r}
                        </span>
                      ))}
                    </div>
                  )}
                  {(it.usable_in_category_slugs?.length || it.allowed_background_color_ids?.length || it.min_contrast_against_color_ids?.length) > 0 && (
                    <p className="text-[11px] text-[#6B6358] mt-2 italic">
                      {it.usable_in_category_slugs?.length > 0 && `Categories: ${it.usable_in_category_slugs.join(", ")}`}
                      {it.allowed_background_color_ids?.length > 0 && ` · ${it.allowed_background_color_ids.length} bg constraint(s)`}
                      {it.min_contrast_against_color_ids?.length > 0 && ` · contrast vs ${it.min_contrast_against_color_ids.length}`}
                    </p>
                  )}
                  {it.description && (
                    <p className="text-xs text-[#3D3A35] mt-2 line-clamp-2">{it.description}</p>
                  )}
                  {canManage && (
                    <RowActions
                      onEdit={() => openEdit(it)}
                      onDelete={() => remove(it.color_id)}
                      editTestId={`${BRAND_KIT.colorEditPrefix}${it.color_id}`}
                      deleteTestId={`${BRAND_KIT.colorDeletePrefix}${it.color_id}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
